import React, { useEffect } from 'react';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import './ListMentions.scss';
import Loader from '../Loader/Loader';
import { updateMention } from '../../APIClient/mentions';
import { getBrandId } from '../../Helpers/user_helpers';
import { isAdminControlMode } from '../../Helpers/ui_helpers';

import MentionResult from '../Mentions/MentionResult';
import useElementInView from '../../CustomHooks/useElementInView';

const ListMentions = props => {
  const { user, ui, mentions, isLoadingMentions, isSyncingList, loadMentions } = props;

  if (isSyncingList) return null;

  const history = useHistory();
  const [inViewRef, isNearView] = useElementInView({ rootMargin: '200px 0px' });

  useEffect(() => {
    if (isNearView) {
      // Load mentions when the component is near the viewport
      loadMentions();
    }
  }, [isNearView]);

  let columns = [[], [], []];
  const columnChunks = _.chunk(mentions, 3);
  columnChunks.forEach(chunk => {
    chunk[0] && columns[0]?.push(chunk[0]);
    chunk[1] && columns[1]?.push(chunk[1]);
    chunk[2] && columns[2]?.push(chunk[2]);
  });

  const removeMentionIfAllowedTo = isAdminControlMode(ui)
    ? async mention => {
        const confirm = window.confirm('Are you sure you want to remove this mention?');
        if (confirm) {
          await updateMention(mention, { isHidden: true });
          cogoToast.success('Mention Removed');
          props.syncList();
        }
      }
    : null;

  const goToAccountSettings = () => {
    props.ensureLoggedInThenCall(() => {
      history.push('/settings?tab=Advanced+Analytics');
    });
  };

  const goToSocialMentionsFeed = () => {
    props.ensureLoggedInThenCall(() => {
      history.push('/mentions');
    });
  };

  const hasMentions = mentions?.length > 0;

  return (
    <div className='list-mentions-container' ref={inViewRef}>
      <div className='section-header-container'>
        <div className='section-header'>See them in Action</div>
        <div className='section-subheader'>
          {props.isBrandList
            ? `A quick peek into the content these creators are posting about your brand`
            : `A quick peek into the brands these creators are talking about`}
        </div>
      </div>
      <div className='list-mentions-loader-status'>
        {isLoadingMentions && <Loader size={100} />}
        {!isLoadingMentions && !hasMentions && (
          <div className='no-results'>
            Nothing to see yet! Try configuring more Similar Brands in
            <span onClick={goToAccountSettings} className='internal-link'>
              account settings
            </span>
            or view
            <span onClick={goToSocialMentionsFeed} className='internal-link'>
              all of your mentions
            </span>
          </div>
        )}
      </div>
      <div className='mention-results-columns'>
        {columns.map((column, columnIdx) => {
          return (
            <div key={columnIdx} className='mention-results-column'>
              {column.map(mention => {
                return (
                  <MentionResult
                    key={mention.id}
                    mention={mention}
                    user={user}
                    ui={ui}
                    curFilters={{ Brand_id: getBrandId(user) }}
                    openArtistModal={props.openArtistModal}
                    openChatOverlay={props.openChatOverlay}
                    openAuthModal={props.openAuthModal}
                    removeMentionIfAllowedTo={removeMentionIfAllowedTo}
                  />
                );
              })}
            </div>
          );
        })}
        {hasMentions && (
          <div onClick={goToSocialMentionsFeed} className='view-more-mention-results'>
            <div className='alert'>
              View all mentions in your social mentions feed
              <FontAwesomeIcon icon={faExternalLink} className='arrow-right' />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ListMentions.propTypes = {
  ui: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  list: PropTypes.object.isRequired,
  isEditing: PropTypes.bool.isRequired,
  isBrandList: PropTypes.bool,
  syncList: PropTypes.func.isRequired,
  openArtistModal: PropTypes.func.isRequired,
  openChatOverlay: PropTypes.func.isRequired,
  openAuthModal: PropTypes.func.isRequired,
  ensureLoggedInThenCall: PropTypes.func.isRequired,
  isSyncingList: PropTypes.bool.isRequired,
  loadMentions: PropTypes.func.isRequired,
  isLoadingMentions: PropTypes.bool
};

export default ListMentions;
